import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  Grid,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FilledInput
} from '@mui/material';
import { Dayjs } from 'dayjs';
import DateRangePicker from '../DateRangePicker/DateRangePicker';
import AppSelect from '../AppSelect/AppSelect';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import './ArtRequestOption.scss';
// import { useAdmin } from '../../providers/Admin';
import { useSearchOptions } from '../../providers/SearchOptions';
import { useSimArtPilot } from '../../providers/SimArtPilotProvider';
import {
  SIM_CLASS_OPTIONS,
  SIM_CLASS_REVERSE_MAP,
  CLASS_INFO,
  SIM_PRIORITY_OPTIONS
} from '../../constants/simArt';
import { useAdmin } from '../../providers/Admin';

export interface ArtRequestOptionProps {
  initialValues?: ArtRequestOptionValues;
  onChange?: (values: ArtRequestOptionValues) => void;
  onSave?: (values: ArtRequestOptionValues) => void;
  onLocationChange?: (location: string | null) => void;
  valuesToEdit?: ArtRequestOptionValues | null;
  saveCount?: number;
  maxSaves?: number;
  title?: string;
  mode?: 'ART' | 'SIM';
  SimMonth?: number;
  SimYear?: number;
  usedPriorities?: string[];
  hasSavedRequests?: boolean; // New prop to track if there are saved requests
  disabledDates?: string[]; // Array of disabled dates in 'YYYY-MM-DD' format
  // allow caller to pass mode-specific option maps
  options?: string[];
  reverseMap?: Record<string, string>;
  classInfo?: Record<string, string>;
  priorityOptions?: string[];
}

export interface ArtRequestOptionValues {
  id?: number;

  location?: string;
  earlyScheduling?: boolean;
  type?: string;
  classOption?: string;
  priority?: string;
  range?: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  };
  comment?: string;
}
export default function ArtRequestOption(props: ArtRequestOptionProps = {}) {
  const {
    initialValues = {},
    valuesToEdit = null,
    onSave,
    onChange,
    saveCount = 0,
    maxSaves = 3,
    mode,
    SimMonth,
    SimYear,
    usedPriorities = [],
    hasSavedRequests = false,
    options = SIM_CLASS_OPTIONS,
    reverseMap = SIM_CLASS_REVERSE_MAP,
    classInfo = CLASS_INFO,
    priorityOptions = SIM_PRIORITY_OPTIONS,
    onLocationChange
  } = props;
  const adminContext = useAdmin();
  const { baseOptions } = useSearchOptions();
  const { artBaseLocations } = useSimArtPilot();

  // For SIM mode: Remove the last item from baseOptions array
  const filteredBaseOptions = baseOptions.slice(0, -1);

  // For ART mode: Use the artBaseLocations from API and extract labels
  const artLocationOptions =
    artBaseLocations?.map((location: any) => location.label) || [];

  // Choose the appropriate location options based on mode
  const locationOptions =
    mode === 'ART' ? artLocationOptions : filteredBaseOptions;

  // Helper function to convert location code to label for ART mode
  const getLocationLabel = useCallback(
    (locationCode: string) => {
      if (mode !== 'ART' || !locationCode) return locationCode;
      const foundLocation = artBaseLocations?.find(
        (loc: any) => loc.code === locationCode
      );
      return foundLocation ? foundLocation.label : locationCode;
    },
    [mode, artBaseLocations]
  );

  const [editingId, setEditingId] = useState<number | undefined>(undefined);
  // For ART mode, convert the initial location code to label so dropdown shows correctly
  const [location, setLocation] = useState(() => {
    const initialLoc = initialValues.location ?? '';
    return mode === 'ART' ? getLocationLabel(initialLoc) : initialLoc;
  });
  const [earlyScheduling, setEarlyScheduling] = useState(false);
  const [type, setPlanType] = useState('Plan');

  const [classOption, setClassOption] = useState('');

  const [priority, setPriority] = useState(() => {
    if (initialValues.priority) return initialValues.priority;
    return '';
  });
  const [comment, setComment] = useState(initialValues.comment ?? '');
  const [sendDateAnchorEl, setSendDateAnchorEl] =
    useState<HTMLDivElement | null>(null);
  const sendDateAnchorRef = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState<{
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }>({ startDate: null, endDate: null });
  const [pickerOpen, setPickerOpen] = useState(false);
  function handleClick() {
    setSendDateAnchorEl(sendDateAnchorRef.current);
    setPickerOpen(true);
  }

  const showFields = mode === 'SIM' || mode === 'ART';

  // Check if PM schedules are available for the selected location (currently only YYZ)
  const isPMAvailable = useCallback(() => {
    if (mode !== 'ART' || !location) return true;
    // PM schedules are currently only available in YYZ (Toronto)
    return location.toUpperCase().includes('YYZ');
  }, [mode, location]);

  useEffect(() => {
    // Check if we should reset: either valuesToEdit is null/undefined, or it's an empty object (after cancel)
    const shouldReset =
      !valuesToEdit ||
      (valuesToEdit && !valuesToEdit.id && !valuesToEdit.location);
    if (shouldReset) {
      setEditingId(undefined);
      // For ART mode, convert code to label; for SIM mode, use as-is
      const resetLocation =
        mode === 'ART'
          ? getLocationLabel(initialValues.location ?? '')
          : initialValues.location ?? '';
      setLocation(resetLocation);
      setEarlyScheduling(false);
      setPlanType('Plan');
      setClassOption(''); // Reset to empty string
      setRange({ startDate: null, endDate: null });
      setComment(initialValues.comment ?? '');
      setPriority(''); // Reset to empty string

      return;
    }

    // At this point, valuesToEdit is definitely not null and has valid data
    if (valuesToEdit) {
      setEditingId(valuesToEdit.id ?? undefined);
      // For ART mode, convert the stored code to label for display
      const editLocation =
        mode === 'ART'
          ? getLocationLabel(valuesToEdit.location ?? '')
          : valuesToEdit.location ?? '';
      setLocation(editLocation);
      setEarlyScheduling(valuesToEdit.earlyScheduling ?? false);
      setPlanType(valuesToEdit.type ?? 'Plan');
      setClassOption((valuesToEdit.classOption as string) ?? '');
      setPriority((valuesToEdit.priority as string) ?? '');
      setRange(valuesToEdit.range ?? { startDate: null, endDate: null });
      setComment(valuesToEdit.comment ?? '');
    }
  }, [
    valuesToEdit,
    initialValues.classOption,
    initialValues.comment,
    initialValues.location,
    initialValues.priority,
    usedPriorities,
    getLocationLabel,
    mode
  ]);

  // Handle artBaseLocations loading after component mount - update location display
  useEffect(() => {
    if (
      mode === 'ART' &&
      artBaseLocations &&
      initialValues.location &&
      !valuesToEdit
    ) {
      const convertedLocation = getLocationLabel(initialValues.location);
      if (convertedLocation !== location && convertedLocation !== '') {
        setLocation(convertedLocation);
      }
    }
  }, [
    artBaseLocations,
    initialValues.location,
    mode,
    valuesToEdit,
    location,
    getLocationLabel
  ]);

  // When location changes, validate if current class selection is still valid
  useEffect(() => {
    if (mode !== 'ART' || !location || !classOption) return;

    const pmAvailable = isPMAvailable();
    const isPMSelected = classOption.toUpperCase() === 'PM';
    const isAnyTimeSelected =
      classOption.toUpperCase().includes('ANY TIME') ||
      classOption.toUpperCase().includes('ANYTIME');

    // If PM or Any time is selected but not available for this location, clear it
    if ((isPMSelected || isAnyTimeSelected) && !pmAvailable) {
      setClassOption('');
      setPriority('');
    }
  }, [location, classOption, mode, isPMAvailable]);

  useEffect(() => {
    onChange?.({
      id: editingId,
      location,
      earlyScheduling,
      type,
      classOption,
      priority,
      range,
      comment
    });
  }, [
    editingId,
    location,
    earlyScheduling,
    type,
    classOption,
    priority,
    range,
    comment,
    onChange
  ]);
  //  const isLocked = !editingId && saveCount >= maxSaves;
  const getPriorityOptions = (planType: string) => {
    // return provided priorityOptions (ART or SIM) by caller
    return priorityOptions;
  };
  // const isLocationRequired = mode === 'ART';
  const missingRequiredField =
    // (isLocationRequired && !location) ||
    (!range.startDate && !range.endDate) ||
    !classOption ||
    classOption === '' ||
    !priority ||
    priority === '';
  const saveDisabled =
    (!editingId && saveCount >= maxSaves) || missingRequiredField;

  // lock all controls when max saves reached and user is not editing an existing entry
  const isLocked = !editingId && saveCount >= maxSaves;

  const formatDateDisplay = (
    startDate: Dayjs | null,
    endDate: Dayjs | null,
    planType: string
  ) => {
    if (!startDate) {
      return 'Choose date';
    }

    // if (planType === 'Plan') {
    //   return startDate.format('MMM D, YYYY');
    // }
    if (endDate) {
      if (startDate.isSame(endDate, 'day')) {
        // If start and end date are the same, show only one date
        return `${startDate.format('MMM D, YYYY')}`;
      }
      if (
        startDate.month() === endDate.month() &&
        startDate.year() === endDate.year()
      ) {
        return `${startDate.format('MMMM D')}-${endDate.format('D, YYYY')}`;
      } else {
        return `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`;
      }
    }

    return `${startDate.format('MMM D, YYYY')}`;
  };

  function handleSave() {
    const payload: ArtRequestOptionValues = {
      id: editingId ?? undefined,
      location,
      earlyScheduling,
      type,
      classOption:
        classOption && classOption !== 'Choose Class'
          ? reverseMap[classOption] || classOption
          : '',
      priority: priority && priority !== 'Choose Priority' ? priority : '',
      range,
      comment
    };

    if (onSave) onSave(payload);
    setEditingId(undefined);
  }

  const rawPriorities = getPriorityOptions(type) as Array<
    { label?: string; value?: string } | string
  >;
  const usedSet = new Set((usedPriorities || []).map((s) => String(s).trim()));
  const visiblePriorities = rawPriorities
    .map((opt) =>
      typeof opt === 'object'
        ? String(
            (opt as { label?: string; value?: string }).label ??
              (opt as { label?: string; value?: string }).value ??
              ''
          )
        : String(opt)
    )
    .map((s) => s.trim())
    .filter(
      (label) => String(priority ?? '').trim() === label || !usedSet.has(label)
    );

  const selectedPriorityOption =
    priority && priority.trim() ? priority.trim() : '';

  return (
    <Card className="art-request-option-wrapper">
      <CardContent>
        <Typography className="art-request-option" variant="h4" gutterBottom>
          {mode === 'ART' ? 'ART Request Options' : 'SIM Request Options'}
        </Typography>
        <Typography className="select-prefered-class" gutterBottom>
          Please select <b>three</b> preferred SIM dates based in order of
          preference and availability. Consideration is based on factors such as
          seniority, SIM availability, travel logistics, and sound economic
          principles.
        </Typography>
        {(() => {
          const file =
            adminContext.documentsAndTypes?.filestats?.filter((file) => {
              return file.documentType === 'ART_SCHEDULE';
            }) || [];
          return mode === 'ART' && file.length > 0 ? (
            file[0]?.fileURL ? (
              <div style={{ marginBottom: 16 }}>
                <a
                  className="class-schedule-link"
                  href={file[0]?.fileURL}
                  aria-label={`download ${file[0]?.fileName} file`}
                  rel="noreferrer"
                  target="_blank"
                >
                  Click here for latest ART Class schedule
                </a>
              </div>
            ) : file[0]?.fileName ? (
              <div style={{ marginBottom: 16 }}>
                <span className="class-schedule-link">{file[0]?.fileName}</span>
              </div>
            ) : null
          ) : null;
        })()}

        <Grid
          container
          spacing={1}
          className="art-location"
          sx={{ mb: 1, ml: 0, display: 'flex', flexDirection: 'column' }}
        >
          {mode === 'ART' && (
            <div className="art-location-select">
              <Tooltip
                title={
                  hasSavedRequests
                    ? 'Delete all saved requests to change the base location'
                    : ''
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                      padding: '12px 20px',
                      maxWidth: '300px'
                    }
                  }
                }}
              >
                <div>
                  <AppSelect
                    selectLabel="Choose ART Location"
                    name="select"
                    fillElements={
                      locationOptions.length > 0
                        ? ['Select location to continue', ...locationOptions]
                        : ['Loading locations...']
                    }
                    selectedItem={location || 'Select location to continue'}
                    multiSelect={false}
                    disabled={
                      hasSavedRequests ||
                      (mode === 'ART' && locationOptions.length === 0)
                    }
                    onChange={(value) => {
                      if (hasSavedRequests) return; // Prevent changes when there are saved requests
                      if (value === 'Select location to continue') {
                        setLocation('');
                        // notify parent that location was cleared
                        onLocationChange?.(null);
                        return;
                      }

                      // For ART mode, we need to find the corresponding code from the label
                      let locationCode = value;
                      if (mode === 'ART') {
                        const selectedLocation = artBaseLocations?.find(
                          (loc: any) => loc.label === value
                        );
                        locationCode = selectedLocation
                          ? selectedLocation.code
                          : value;
                      }

                      // For display: keep the label (value) in local state
                      setLocation(value);
                      // For persistence: store the code in parent state
                      const codeToStore = mode === 'ART' ? locationCode : value;
                      onLocationChange?.(codeToStore);
                      if (!value) {
                        setRange({ startDate: null, endDate: null });
                        setClassOption(SIM_CLASS_OPTIONS[0]);
                        setPriority(SIM_PRIORITY_OPTIONS[0]);
                      }
                    }}
                  />
                </div>
              </Tooltip>
            </div>
          )}
          {showFields && (
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: '10px'
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={isLocked}
                    checked={earlyScheduling}
                    onChange={(e) => setEarlyScheduling(e.target.checked)}
                  />
                }
                label={
                  <p className="early-scheduling-text">
                    Pilots can be scheduled up to 90 days before the activity's
                    due date. Check this box if you prefer early scheduling.{' '}
                    <br />
                    <p>(note: not guaranteed)</p>
                  </p>
                }
              />
            </Grid>
          )}
        </Grid>
        {showFields && (
          <RadioGroup
            row
            value={type}
            onChange={(e) => {
              setPlanType(e.target.value);
            }}
            sx={{
              mb: 2,
              color: '#005078',
              '& .Mui-checked': { color: '#005078' }
            }}
          >
            <FormControlLabel
              sx={{ color: '#005078' }}
              value="Plan"
              disabled={isLocked}
              control={
                <Radio
                  disabled={isLocked}
                  sx={{
                    '& .MuiSvgIcon-root': { color: '#9E9E9E' },
                    fontFamily: 'Open Sans',
                    '&.Mui-checked, &.Mui-checked .MuiSvgIcon-root': {
                      color: '#005078'
                    }
                  }}
                />
              }
              label="Plan"
            />
            <FormControlLabel
              sx={{ color: '#005078' }}
              value="Avoid"
              disabled={isLocked}
              control={
                <Radio
                  disabled={isLocked}
                  sx={{
                    '& .MuiSvgIcon-root': { color: '#9E9E9E' },
                    '&.Mui-checked, &.Mui-checked .MuiSvgIcon-root': {
                      color: '#005078'
                    }
                  }}
                />
              }
              label="Avoid"
            />
          </RadioGroup>
        )}

        {showFields && (
          <div className="art-request-option-fields">
            <div className="art-class-select">
              <div
                className={`date-range-picker-fields${range.startDate ? ' has-value' : ''}`}
                ref={sendDateAnchorRef}
                onClick={() => {
                  if (isLocked) return;
                  handleClick();
                }}
                tabIndex={isLocked ? -1 : 0}
                aria-disabled={isLocked}
                role="button"
              >
                <span className="date-picker-label">Date</span>
                <span
                  className="date-value-text"
                  style={{
                    color: isLocked ? '#999' : 'inherit'
                  }}
                >
                  {formatDateDisplay(range.startDate, range.endDate, type)}
                </span>
                <div className="dropdown-icon-container">
                  <div onClick={handleClick} className="icon_container">
                    <i className="icon-chevron-thin-down" />
                  </div>
                </div>
              </div>

              <DateRangePicker
                disabled={isLocked}
                startDate={range.startDate}
                endDate={range.endDate}
                onDateChange={(startDate, endDate) => {
                  setRange({ startDate, endDate });
                  setPickerOpen(false);
                }}
                // prevent opening when locked
                open={pickerOpen && !isLocked}
                anchorEl={sendDateAnchorEl}
                onClose={() => setPickerOpen(false)}
                anchorRef={sendDateAnchorRef}
                disableMonthNavigation={mode === 'SIM'}
                // selectionMode={type === 'Plan' ? 'single' : 'range'}
                selectionMode="range"
                popoverWidth="300px"
                initialMonth={SimMonth}
                initialYear={SimYear}
                mode={mode}
              />
            </div>

            <div className="art-class-select">
              {mode === 'ART' && location ? (
                // Custom Select with disabled items support for ART mode
                <FormControl className="select-dropdown" fullWidth>
                  <InputLabel
                    id="class-select-label"
                    sx={{
                      color: '#005078',
                      fontSize: '14px',
                      fontWeight: 600,
                      '&.Mui-focused': { color: '#005078' }
                    }}
                  >
                    Select Class
                  </InputLabel>
                  <Select
                    labelId="class-select-label"
                    value={classOption || 'Choose Class'}
                    onChange={(e) => {
                      if (isLocked) return;
                      const value = e.target.value;
                      if (value === 'Choose Class') {
                        setClassOption('');
                        return;
                      }
                      setClassOption(value);
                    }}
                    disabled={isLocked}
                    input={<FilledInput />}
                    disableUnderline
                    sx={{
                      backgroundColor: isLocked ? '#f5f5f5' : 'white',
                      '& .MuiFilledInput-input': {
                        padding: '25px 12px 8px',
                        fontSize: '14px'
                      },
                      '&:before, &:after': {
                        display: 'none'
                      }
                    }}
                  >
                    <MenuItem value="Choose Class">Choose Class</MenuItem>
                    {options.map((opt) => {
                      const isPM = opt.toUpperCase() === 'PM';
                      const isAnyTime =
                        opt.toUpperCase().includes('ANY TIME') ||
                        opt.toUpperCase().includes('ANYTIME');
                      const pmAvailable = isPMAvailable();
                      const isDisabled = (isPM || isAnyTime) && !pmAvailable;

                      return (
                        <MenuItem
                          key={opt}
                          value={opt}
                          disabled={isDisabled}
                          sx={{
                            '&.Mui-disabled': {
                              opacity: 0.4,
                              color: '#999',
                              cursor: 'not-allowed'
                            }
                          }}
                        >
                          {opt}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              ) : (
                // Use AppSelect for SIM mode or when no location selected
                <AppSelect
                  selectLabel="Select Class"
                  fillElements={['Choose Class', ...options]}
                  selectedItem={classOption || 'Choose Class'}
                  multiSelect={false}
                  disabled={isLocked}
                  onChange={(value) => {
                    if (isLocked) return;
                    if (value === 'Choose Class') {
                      setClassOption('');
                      return;
                    }
                    setClassOption(value);
                  }}
                />
              )}

              {/* Show info message when PM is not available for selected location */}
              {mode === 'ART' && location && !isPMAvailable() && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#FFF4E5',
                    border: '1px solid #FFB020',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '12px',
                    color: '#663C00'
                  }}
                >
                  <InfoOutlined
                    fontSize="small"
                    style={{ marginRight: '8px', fontSize: '16px' }}
                  />
                  <span>PM schedules are currently only available in YYZ.</span>
                </div>
              )}

              {classInfo[reverseMap[classOption]] && (
                <p className="class-time-info">
                  <Tooltip
                    title={classInfo[reverseMap[classOption]] ?? ''}
                    arrow
                    placement="bottom"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                          padding: '12px 20px',
                          maxWidth: '300px'
                        }
                      }
                    }}
                  >
                    <InfoOutlined fontSize="small" />
                  </Tooltip>
                  <p>{classInfo[reverseMap[classOption]] ?? ''}</p>
                </p>
              )}
            </div>

            <div className="art-class-select">
              <AppSelect
                selectLabel="Choose Priority"
                fillElements={['Choose Priority', ...visiblePriorities]}
                selectedItem={selectedPriorityOption || 'Choose Priority'}
                multiSelect={false}
                disabled={isLocked}
                onChange={(value) => {
                  if (isLocked) return;
                  if (value === 'Choose Priority') {
                    setPriority('');
                    return;
                  }
                  const raw =
                    typeof value === 'object'
                      ? value.value ?? value.label ?? value
                      : value ?? '';
                  setPriority(String(raw));
                }}
              />
            </div>
            <div className="art-class-save">
              <Button
                className="save_button"
                fullWidth
                onClick={handleSave}
                disabled={saveDisabled}
                sx={{
                  backgroundColor: saveDisabled ? '#ccc' : '#005078',
                  color: 'white',
                  textTransform: 'none',
                  '&:disabled': { backgroundColor: '#ccc', color: '#666' }
                }}
              >
                {editingId ? 'Update' : 'Save'}
              </Button>
              {isLocked && (
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999'
                  }}
                >
                  <Tooltip
                    title="You have reached the maximum number of saved options"
                    arrow
                    placement="bottom"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                          padding: '12px 20px',
                          maxWidth: '300px'
                        }
                      }
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <InfoOutlined fontSize="small" />
                      <span
                        style={{
                          fontSize: '12px',
                          margin: '1px',
                          fontWeight: 600,
                          justifyContent: 'center',
                          color: '#333'
                        }}
                      >
                        You have already selected 3 options
                      </span>
                    </span>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
